import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import * as Icon from '@mui/icons-material/';
import { toast } from 'react-toastify';
import { Button } from '../../../Styles/ScreenStyle';
import PapelController from '../../../../controllers/PapelController';

interface PapelItem {
  RoleId: number;
  Nome: string;
  marcado: string;
}

/**
 * Os papéis de um usuário, dentro da tela de cadastro.
 *
 * GRAVA SEPARADO do resto do cadastro, com botão próprio, e isso é deliberado: são duas
 * chamadas diferentes à API e uma pode ser recusada sem a outra. Um botão só criaria o
 * estado em que o nome foi salvo, o papel não, e a tela diz "salvo com sucesso".
 *
 * A recusa que mais vai aparecer aqui é a trava do último administrador. Ela vem da
 * procedure, em português, e é mostrada inteira — é ela que diz o que fazer a seguir.
 */
export function PapeisDoUsuario({ usuarioId }: { usuarioId: number | null }) {
  const [papeis, setPapeis] = useState<PapelItem[]>([]);
  const [marcados, setMarcados] = useState<Set<number>>(new Set());
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (!usuarioId) return;
    carregar(usuarioId);
  }, [usuarioId]);

  const carregar = async (id: number) => {
    setCarregando(true);
    const resposta = await PapelController.DoUsuario(id);

    if (Array.isArray(resposta.Data)) {
      setPapeis(resposta.Data);
      setMarcados(
        new Set(
          resposta.Data.filter((p: PapelItem) => p.marcado === 'S').map(
            (p: PapelItem) => p.RoleId
          )
        )
      );
    }
    setCarregando(false);
  };

  const alternar = (roleId: number) => {
    setMarcados((anterior) => {
      const novo = new Set(anterior);
      if (novo.has(roleId)) novo.delete(roleId);
      else novo.add(roleId);
      return novo;
    });
  };

  const salvar = async () => {
    if (!usuarioId) return;

    setCarregando(true);
    const resposta = await PapelController.DoUsuarioGravar(
      usuarioId,
      Array.from(marcados).map(String)
    );
    setCarregando(false);

    if (resposta.StatusCode === 204) {
      toast('Papéis do usuário atualizados.', { type: 'success' });
      return;
    }

    toast(resposta?.Data ?? 'Não foi possível gravar os papéis.', {
      type: 'error',
      autoClose: 12000,
    });

    // A gravação foi recusada: a tela tem de voltar a mostrar o que está no banco, e não
    // o que a pessoa marcou. Sem isto ela sai acreditando que o papel foi tirado.
    carregar(usuarioId);
  };

  if (!usuarioId) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant='h6'>Papéis de acesso</Typography>
        <Alert severity='info' sx={{ mt: 1 }}>
          Salve o usuário primeiro. Os papéis são atribuídos depois, na edição.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant='h6'>Papéis de acesso</Typography>
      <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary' }}>
        O usuário enxerga a soma das telas de todos os papéis marcados. Sem nenhum papel,
        ele entra no sistema e não vê tela nenhuma.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {papeis.map((p) => (
          <FormControlLabel
            key={p.RoleId}
            sx={{ minWidth: '18rem' }}
            control={
              <Checkbox
                checked={marcados.has(p.RoleId)}
                disabled={carregando}
                onChange={() => alternar(p.RoleId)}
              />
            }
            label={p.Nome}
          />
        ))}
      </Box>

      <Button
        variant='contained'
        color='success'
        startIcon={<Icon.Save />}
        onClick={salvar}
        disabled={carregando}
        sx={{ mt: 1 }}
      >
        Salvar papéis
      </Button>
    </Box>
  );
}
